<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

// types
import type { TableOptions } from '@/models/general/table'
import { $getSigns, $updateSign } from '@/api/signs'


interface IProps {
  modelValue?: boolean,
  sign: any,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  sign: null,
})
interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onRestore'): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

const deletedSignReprints = ref<any[]>([])
const deletedSignReprintsLoading = ref<boolean>(false)
const selectedDeletedSignReprintsIds = ref<number[]>([])
const deletedSignReprintsCount = ref<number>(0)

const loading = ref<boolean>(false)
const restoreSignReprintsBtnLoading = ref<boolean>(false)

const isMounted = ref<boolean>(false)

const tableHeaders = [
  { key: 'modified_date', title: 'Date', sortable: false, align: '' },
  { key: 'code', title: 'Code', sortable: false, align: '' },
]

const deletedSignReprintsTableOptions = ref<TableOptions>({
  itemsPerPage: 10,
  page: 1,
  sortBy: []
})

const itemsPerPageOptions = [
  { value: 10, title: '10' },
  { value: 15, title: '15' },
  { value: 20, title: '20' },
  { value: 50, title: '50' },
]

const onUpdateTableOptions = async ({ page, itemsPerPage, sortBy }: TableOptions) => {
  deletedSignReprintsTableOptions.value.page = page
  deletedSignReprintsTableOptions.value.itemsPerPage = itemsPerPage
  deletedSignReprintsTableOptions.value.sortBy = sortBy
  await getDeletedSignReprints()
}

const getDeletedSignReprints = async () => {
  if (!props.sign?.id) return

  loading.value = true
  try {
    const res = await $getSigns({
      is_deleted_reprint_list: true, // 'id', 'code', 'modified_date' fields only
      reprint_root_sign: props.sign.id,
      is_removed: true,
      ordering: '-modified'
    })
    deletedSignReprints.value = res.data.results
    deletedSignReprintsCount.value = res.data.count
  } catch (e: any) {
    // If page doesn't exist goes to 1 page
    if (e.response.status === 404) {
      deletedSignReprintsTableOptions.value.page = 1
      await getDeletedSignReprints()
    } else {
      console.log(e)
    }
    console.log(e)
  } finally {
    loading.value = false
  }
}

const restoreSignReprints = async () => {
  const deletedSignId = selectedDeletedSignReprintsIds.value[0]
  if (!deletedSignId) return

  restoreSignReprintsBtnLoading.value = true
  try {
    await $updateSign(deletedSignId, { is_removed: false })
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Sign Reprint restored successfully!'
    })
    selectedDeletedSignReprintsIds.value = []
    await getDeletedSignReprints()
    emit('onRestore')
  } catch (e) {
    console.log(e)
  } finally {
    restoreSignReprintsBtnLoading.value = false
  }
}

const closeDialog = () => {
  emit('update:modelValue', !props.modelValue)
}

const onClose = async () => {
  emit('onClose')
}

onMounted(async () => {
  isMounted.value = true
})

watch(() => props.modelValue, async () => {
  // On show dialog
  if (props.modelValue) {
    await getDeletedSignReprints()
  }
  // on hide Dialog
  else {
  }
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    :persistent="!!restoreSignReprintsBtnLoading || !!loading"
    max-width="1000"
    class="default-dialog"
    @after-leave="onClose"
    @update:modelValue="emit('update:modelValue', $event)"
  >
    <VBtn
      variant="elevated"
      density="compact"
      icon="tabler-x"
      :disabled="!!restoreSignReprintsBtnLoading || !!loading"
      class="v-dialog-close-btn"
      @click="closeDialog"
    />

    <VCard>
      <template #title>
        Deleted Sign Reprints
      </template>
      <template #subtitle>
        {{ sign?.short_name }}
      </template>
      <VCardText>
        <VContainer fluid>
          <VOverlay
            v-model="loading"
            persistent
            absolute
            contained
            class="align-center justify-center"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </VOverlay>
          <VRow>
            <VCol
              cols="12"
            >
              <VDataTableServer
                v-model="selectedDeletedSignReprintsIds"
                v-model:page="deletedSignReprintsTableOptions.page"
                :headers="[...tableHeaders]"
                :items="[...deletedSignReprints]"
                :items-length="deletedSignReprintsCount"
                :items-per-page="deletedSignReprintsTableOptions.itemsPerPage"
                :loading="!!deletedSignReprintsLoading"
                :select-strategy="'single'"
                density="compact"
                hover
                item-value="id"
                no-data-text="Deleted Sign Reprints not found"
                show-select
                @update:options="onUpdateTableOptions"
              >
                <template #top>
                  <div class="d-flex align-center justify-space-between mb-4">
                    <VChip>
                      {{ selectedDeletedSignReprintsIds.length }} sign reprint(s) selected
                    </VChip>
                    <div>
                      <VBtn
                        :disabled="!selectedDeletedSignReprintsIds.length || !!restoreSignReprintsBtnLoading"
                        class="mr-2"
                        color="success"
                        size="small"
                        @click="restoreSignReprints()"
                      >
                        Restore
                        <VProgressCircular
                          v-if="restoreSignReprintsBtnLoading"
                          :width="2"
                          class="ml-1"
                          color="success"
                          indeterminate
                          size="x-small"
                        />
                      </VBtn>
                    </div>
                  </div>
                </template>
              </VDataTableServer>
            </VCol>
          </VRow>
        </VContainer>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss">

</style>
